import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl, fmt } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { useCart } from "../lib/cart";
import { Trash2, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, remove, total } = useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
      toast.error("Enter a valid email for your receipt & downloads");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((p) => p.id),
        email,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <div className="App relative">
      <div className="vm-grain" />
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12 sm:py-16 relative z-10">
        <h1 className="font-head text-3xl sm:text-4xl tracking-tight font-light">Your cart</h1>

        {items.length === 0 ? (
          <div className="mt-12 text-center py-20 border border-dashed border-white/10 rounded-2xl">
            <p className="text-zinc-400">Your cart is empty.</p>
            <Link to="/" data-testid="empty-browse-link" className="inline-block mt-5 bg-white text-black px-6 py-3 rounded-md font-medium hover:bg-zinc-200 transition-colors">
              Browse products
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8 mt-10">
            <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
              {items.map((p) => (
                <div key={p.id} className="vm-glass rounded-xl p-4 flex items-center gap-4" data-testid={`cart-item-${p.id}`}>
                  <img src={mediaUrl(p.cover_path)} alt={p.title} className="w-20 h-16 object-cover rounded-md border border-white/10" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-head font-medium truncate">{p.title}</h3>
                    <p className="text-xs text-zinc-500 uppercase tracking-wide mt-1">{p.category}</p>
                  </div>
                  <span className="font-mono">{fmt(p.price)}</span>
                  <button data-testid={`remove-${p.id}`} onClick={() => remove(p.id)} className="text-zinc-500 hover:text-red-400 transition-colors p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="vm-glass rounded-xl p-6 h-fit sticky top-24">
              <h3 className="font-head text-lg">Order summary</h3>
              <div className="flex justify-between mt-5 text-sm text-zinc-400">
                <span>Subtotal ({items.length})</span>
                <span className="font-mono text-white">{fmt(total)}</span>
              </div>
              <div className="flex justify-between mt-2 text-sm text-zinc-400">
                <span>Tax</span><span>—</span>
              </div>
              <div className="border-t border-white/10 my-5" />
              <div className="flex justify-between items-baseline">
                <span className="text-zinc-300">Total</span>
                <span className="font-mono text-2xl" data-testid="cart-total">{fmt(total)}</span>
              </div>
              <label className="block text-xs text-zinc-500 mt-6 mb-2">Email for downloads & receipt</label>
              <input
                data-testid="checkout-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-zinc-950 border border-white/15 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-white/50 transition-colors"
              />
              <button
                data-testid="checkout-button"
                onClick={checkout}
                disabled={loading}
                className="w-full mt-4 inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-3.5 rounded-md font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <><Lock className="w-4 h-4" /> Pay {fmt(total)}</>}
              </button>
              <p className="text-[11px] text-zinc-600 mt-3 text-center">Secured by Stripe · Use card 4242 4242 4242 4242 to test</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
